import { useState } from "react";
import type { FormEvent } from "react";

const INSTITUTIONAL_KEY_ENDPOINT = "/admin/institution/api-key";

export function InstitutionalAdmin() {
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const institution = String(form.get("institution") ?? "").trim();
    setIsGenerating(true);
    setMessage(null);

    try {
      setApiKey(await mockGenerateApiKey(INSTITUTIONAL_KEY_ENDPOINT, institution));
      setMessage("API key generated locally. Save it now; generating another replaces it.");
    } catch (error) {
      setApiKey(null);
      setMessage(error instanceof Error ? error.message : "Unable to generate API key.");
    } finally {
      setIsGenerating(false);
    }
  }

  async function copyApiKey() {
    if (!apiKey) return;
    try {
      await navigator.clipboard.writeText(apiKey);
      setMessage("API key copied to the clipboard.");
    } catch {
      setMessage("Clipboard access was unavailable. Select and copy the key manually.");
    }
  }

  return (
    <section className="mx-auto max-w-4xl">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-mls-600">
        Local administration
      </p>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">
        Institutional Admin
      </h1>
      <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
        Generate a one-time local development key. The institution name is not
        transmitted, persisted, or embedded in the generated key.
      </p>

      <form
        className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
        onSubmit={handleSubmit}
      >
        <label className="block text-sm font-medium text-slate-700">
          Institution name
          <input
            className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 outline-none transition focus:border-mls-600 focus:ring-2 focus:ring-mls-600/20"
            name="institution"
            type="text"
            autoComplete="off"
            required
          />
        </label>
        <button
          className="mt-5 rounded-lg bg-mls-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-mls-600 disabled:opacity-60"
          type="submit"
          disabled={isGenerating}
        >
          {isGenerating ? "Generating…" : "Generate API Key"}
        </button>
      </form>

      {apiKey ? (
        <section className="mt-6 rounded-2xl border border-amber-200 bg-white p-6 shadow-sm">
          <h2 className="font-semibold text-slate-950">Plaintext API key</h2>
          <p className="mt-1 text-xs text-amber-700">This value is displayed only for this generation.</p>
          <textarea
            aria-label="Generated institutional API key"
            className="mt-4 h-24 w-full resize-none rounded-xl bg-slate-950 p-4 font-mono text-xs text-amber-200"
            readOnly
            value={apiKey}
          />
          <button
            className="mt-4 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            type="button"
            onClick={copyApiKey}
          >
            Copy API Key
          </button>
        </section>
      ) : null}

      {message ? <p className="mt-4 text-sm text-slate-600" role="status">{message}</p> : null}
    </section>
  );
}

async function mockGenerateApiKey(endpoint: string, institution: string): Promise<string> {
  await new Promise((resolve) => window.setTimeout(resolve, 300));
  if (endpoint !== INSTITUTIONAL_KEY_ENDPOINT || institution.length < 2) {
    throw new Error("Enter a valid institution name.");
  }

  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const token = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return "mls2_inst_" + token;
}
